import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { ScreenShell } from "./ScreenShell";

const MIN_BODY_FAT = 3;
const MAX_BODY_FAT = 60;
const MIN_WAIST_CM = 30;
const MAX_WAIST_CM = 300;

export interface MeasurementsValue {
  readonly bodyFatPercent?: number;
  readonly waistCm?: number;
}

interface MeasurementsStepProps {
  readonly bodyFatPercent?: number;
  readonly waistCm?: number;
  readonly onContinue: (value: MeasurementsValue) => void;
}

function parseOptional(raw: string, min: number, max: number): number | undefined {
  if (raw.trim() === "") return undefined;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < min || parsed > max) return undefined;
  return parsed;
}

/** Optional body-fat % and waist inputs; both may be left blank. */
export function MeasurementsStep({
  bodyFatPercent,
  waistCm,
  onContinue,
}: MeasurementsStepProps) {
  const [bodyFatDisplay, setBodyFatDisplay] = useState(() =>
    bodyFatPercent !== undefined ? String(bodyFatPercent) : "",
  );
  const [waistDisplay, setWaistDisplay] = useState(() =>
    waistCm !== undefined ? String(waistCm) : "",
  );

  const bodyFatRaw = bodyFatDisplay.trim() === "" ? undefined : Number(bodyFatDisplay);
  const waistRaw = waistDisplay.trim() === "" ? undefined : Number(waistDisplay);
  const bodyFatInvalid =
    bodyFatRaw !== undefined &&
    (!Number.isFinite(bodyFatRaw) || bodyFatRaw < MIN_BODY_FAT || bodyFatRaw > MAX_BODY_FAT);
  const waistInvalid =
    waistRaw !== undefined &&
    (!Number.isFinite(waistRaw) || waistRaw < MIN_WAIST_CM || waistRaw > MAX_WAIST_CM);

  const handleContinue = () => {
    console.log("[Frontend][MeasurementsStep] Optional measurements submitted", {
      hasBodyFat: !bodyFatInvalid && bodyFatRaw !== undefined,
      hasWaist: !waistInvalid && waistRaw !== undefined,
    });
    onContinue({
      bodyFatPercent: bodyFatInvalid
        ? undefined
        : parseOptional(bodyFatDisplay, MIN_BODY_FAT, MAX_BODY_FAT),
      waistCm: waistInvalid ? undefined : parseOptional(waistDisplay, MIN_WAIST_CM, MAX_WAIST_CM),
    });
  };

  return (
    <ScreenShell
      center
      title="Optional: body-fat % and waist"
      subtext="Improves estimate accuracy. Leave blank if unsure — consumer estimates can be inaccurate."
      footer={
        <Button variant="primary" size="lg" className="w-full" onClick={handleContinue}>
          Continue
        </Button>
      }
    >
      <div className="flex flex-col items-center gap-6">
        <div className="flex flex-col items-center gap-1.5">
          <label htmlFor="ob-body-fat" className="text-center text-sm font-medium text-text">
            Body-fat percentage (optional)
          </label>
          <div className="flex items-end justify-center gap-2">
            <input
              id="ob-body-fat"
              type="text"
              inputMode="decimal"
              value={bodyFatDisplay}
              onChange={(e) => setBodyFatDisplay(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="20"
              className="w-32 rounded-2xl border border-border bg-surface px-4 py-3 text-center text-3xl font-bold tabular-nums text-text placeholder:text-text-muted/40 focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <span className="pb-3 text-base font-medium text-text-muted">%</span>
          </div>
          {bodyFatInvalid && (
            <p role="alert" className="text-sm text-danger">
              Enter a value between {MIN_BODY_FAT} and {MAX_BODY_FAT}, or leave blank.
            </p>
          )}
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <label htmlFor="ob-waist" className="text-center text-sm font-medium text-text">
            Waist circumference (optional)
          </label>
          <div className="flex items-end justify-center gap-2">
            <input
              id="ob-waist"
              type="text"
              inputMode="decimal"
              value={waistDisplay}
              onChange={(e) => setWaistDisplay(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="80"
              className="w-32 rounded-2xl border border-border bg-surface px-4 py-3 text-center text-3xl font-bold tabular-nums text-text placeholder:text-text-muted/40 focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <span className="pb-3 text-base font-medium text-text-muted">cm</span>
          </div>
          {waistInvalid && (
            <p role="alert" className="text-sm text-danger">
              Enter a value between {MIN_WAIST_CM} and {MAX_WAIST_CM}, or leave blank.
            </p>
          )}
        </div>
      </div>
    </ScreenShell>
  );
}
